<template>
  <div>
    <div class="cover-page" :class="pageSizeClass">
      <div class="content-title-container">
        <!--span class="content-title-number">
          제{{ item.order_number ? item.order_number : "n" }}장
        </span-->
        <div class="title-container">
          <span class="content-title">
            {{ item.title }}<span v-if="item.subtitle">:</span
            ><span v-if="hasTitlePageComment && !item.subtitle">*</span>
          </span>
          <span class="content-subtitle" v-if="item.subtitle">
            {{ item.subtitle }}<span v-if="hasTitlePageComment">*</span>
          </span>
        </div>
      </div>
      <div class="content-author-container">
        <hr class="content-author-divider" />
        <div class="author-container">
          <div v-for="(author, index) in item.authors" :key="index">
            <span class="content-author name">{{ author.full_name }} </span>
            <span class="content-author name">{{ author.name_eng }} </span>
            <span class="content-author organization"
              >{{ author.organization }}
            </span>
            <span class="content-author organization"
              >{{ author.organization_eng }}
            </span>
          </div>
        </div>
      </div>
      <div class="page-common-footer">
        <hr class="footer-line" />
      </div>
      <div
        v-if="titlePageComments.length > 0"
        class="page-comments-bottom"
      >
        <div class="comments-divider"></div>
        <div
          v-for="(comment, index) in titlePageComments"
          :key="comment.idx || `title-page-${index}`"
          class="page-comment"
        >
          <sup>{{ comment.superscript }}</sup> {{ comment.content }}
        </div>
      </div>
    </div>

    <!-- 초록 페이지 (커버 다음, 본문 앞, 여러 페이지 가능) -->
    <div
      v-for="(absContent, absIndex) in splitAbstractContent"
      :key="'abs-' + absIndex"
      class="abstract-page"
      :class="pageSizeClass"
    >
      <!-- 초록 제목 (첫 페이지만) -->
      <div v-if="absIndex === 0" class="abstract-title-section">
        <h5 class="abstract-title">● 초록(Abstract)</h5>
      </div>

      <!-- 초록 본문 -->
      <div class="abstract-body" v-html="sanitizeHtml(absContent)"></div>

      <!-- 키워드 (마지막 페이지만) -->
      <div
        v-if="item.keyword && absIndex === splitAbstractContent.length - 1"
        class="abstract-keyword-area"
      >
        <span class="keyword-label-box">핵심어</span>
        <span class="keyword-value">{{ item.keyword }}</span>
      </div>

      <div class="page-common-footer">
        <hr class="footer-line" />
      </div>
    </div>

    <div
      v-for="(section, index) in splitContent"
      :key="index"
      class="page"
      :class="[pageSizeClass, getInternalPageClass(index)]"
    >
      <div class="page-content paper-content" v-html="sanitizeHtml(section)"></div>

      <!-- 주석 영역 (페이지 하단) -->
      <div
        v-if="splitComments[index] && splitComments[index].length > 0"
        class="page-comments-bottom"
        :class="getInternalPageClass(index)"
      >
        <div class="comments-divider"></div>
        <div
          v-for="comment in splitComments[index]"
          :key="comment.idx"
          class="page-comment"
        >
          <sup>{{ comment.superscript }}</sup> {{ comment.content }}
        </div>
      </div>

      <div
        class="page-common-footer"
        :class="getInternalPageClass(index)"
      >
        <div v-if="isInternalPageOdd(index)" class="footer-text-container">
          <div>
            <span class="page-number"> {{ getPageNumber(index) }} </span>
          </div>
          <div>
            <span class="footer-text"> {{ group?.title }} </span>
          </div>
        </div>

        <div v-else class="footer-text-container">
          <div>
            <span class="footer-text"> {{ item?.title }} </span>
          </div>
          <div>
            <span class="page-number"> {{ getPageNumber(index) }} </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 참고문헌 페이지 (본문 뒤 별도 페이지, 여러 페이지 가능) -->
    <div
      v-for="(refContent, refIndex) in splitReferencesContent"
      :key="'ref-' + refIndex"
      class="references-page"
      :class="[pageSizeClass, getReferencesInternalPageClass(refIndex)]"
    >
      <h4 v-if="refIndex === 0" class="references-title">참고문헌</h4>
      <div
        class="references-content"
        v-html="sanitizeHtml(refContent)"
      ></div>

      <div
        class="page-common-footer"
        :class="getReferencesInternalPageClass(refIndex)"
      >
        <div v-if="isReferencesPageOdd(refIndex)" class="footer-text-container">
          <div>
            <span class="page-number"> {{ getReferencesPageNumber(refIndex) }} </span>
          </div>
          <div>
            <span class="footer-text"> {{ group?.title }} </span>
          </div>
        </div>

        <div v-else class="footer-text-container">
          <div>
            <span class="footer-text"> {{ item?.title }} </span>
          </div>
          <div>
            <span class="page-number"> {{ getReferencesPageNumber(refIndex) }} </span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable no-console */
import { getSplitContentByHeight } from "@/components/Templates/heightCalculator";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { renderAstToHtml } from "@/utils/paperAst";

const SERVER_URL = process.env.VUE_APP_SERVER_URI;

export default {
  name: "article-paper-page",
  props: {
    item: {
      type: Object,
      required: true,
      default: () => {},
    },
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    startPageNumber: {
      type: Number,
      required: true,
    },
    journal: {
      type: Object,
      required: true,
      default: () => {},
    },
    pageSize: {
      type: String,
      required: false,
      default: "A5",
    },
    // 수동 모드: true면 자동 분할 없이 --- 마커에서만 페이지 나눔
    // false(기본)면 자동 높이 분할 + --- 마커 추가 분할 (하이브리드)
    manualMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      splitContent: [],
      splitComments: [],
      splitReferencesContent: [], // 참고문헌 페이지 분할
      splitAbstractContent: [], // 초록 페이지 분할
      isComponentMounted: false, // 마운트 상태 추적
      isRepaginating: false, // 재페이지네이션 중복 방지
      usingSavedBreakpoints: false, // 저장된 breakpoints 사용 중 여부
    };
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a5";
    },
    pageSizeConfig() {
      const sizes = {
        a4: { width: 794, height: 1122 },
        a5: { width: 559, height: 794 },
      };
      return sizes[this.pageSizeClass] || sizes.a5;
    },
    // 초록 페이지 존재 여부
    hasAbstractPage() {
      return !!(this.item?.abstract || this.item?.abstract_eng);
    },
    // 본문 앞 페이지 수 (커버 + 초록 페이지들)
    prefixPageCount() {
      // 커버 페이지: 1페이지
      // 초록 페이지: splitAbstractContent 배열 길이
      return 1 + this.splitAbstractContent.length;
    },
    // 초록 HTML 포맷 (마크다운 변환 포함)
    formattedAbstract() {
      const parts = [];
      if (this.item?.abstract) {
        parts.push(`<p class="abstract-text-ko">${this.convertMarkdown(this.item.abstract)}</p>`);
      }
      if (this.item?.abstract_eng) {
        parts.push(`<p class="abstract-text-en">${this.convertMarkdown(this.item.abstract_eng)}</p>`);
      }
      return parts.join('');
    },
    // 참고문헌 페이지 존재 여부
    hasReferencesPage() {
      return !!(this.item?.references);
    },
    // 전체 페이지 수 (커버 + 초록 + 본문 + 참고문헌)
    totalPageCount() {
      return this.prefixPageCount + this.splitContent.length + this.splitReferencesContent.length;
    },
    hasTitlePageComment() {
      return (this.item?.comments || []).some((comment) =>
        this.isTitlePageComment(comment)
      );
    },
    titlePageComments() {
      return (this.item?.comments || []).filter((comment) =>
        this.isTitlePageComment(comment)
      );
    },
    // 참고문헌 파싱 (줄바꿈으로 구분, HTML 형식 + 마크다운 변환)
    formattedReferences() {
      if (!this.item?.references) return '';
      const lines = this.item.references
        .split('\n')
        .map(ref => ref.trim())
        .filter(ref => ref.length > 0)
        // "참고문헌" 제목 라인 제거
        .filter(ref => ref !== '참고문헌' && ref !== '참고 문헌');

      return lines.map(ref => {
        return `<p class="reference-item">${this.convertMarkdown(ref)}</p>`;
      }).join('');
    },
    // 본문 HTML 정규화 (AST JSON -> HTML)
    normalizedContent() {
      return this.normalizeContentToHtml(this.item?.content);
    },
    // 콘텐츠 해시 (유효성 검증용)
    contentHash() {
      const content = this.normalizedContent || "";
      return this.simpleHash(content);
    },
    // 저장된 breakpoints (JSON 파싱)
    savedBreakpoints() {
      const bp = this.item?.page_breakpoints;
      if (!bp) return null;
      if (typeof bp === 'string') {
        try {
          return JSON.parse(bp);
        } catch {
          return null;
        }
      }
      return bp;
    },
    // 저장된 메타데이터 (JSON 파싱)
    savedBreakpointsMeta() {
      const meta = this.item?.breakpoints_meta;
      if (!meta) return null;
      if (typeof meta === 'string') {
        try {
          return JSON.parse(meta);
        } catch {
          return null;
        }
      }
      return meta;
    },
    // 저장된 breakpoints 유효성 검사
    isSavedBreakpointsValid() {
      const meta = this.savedBreakpointsMeta;
      const breakpoints = this.savedBreakpoints;
      if (!meta || !breakpoints) return false;

      // 해시 비교
      if (meta.content_hash !== this.contentHash) return false;

      // 페이지 사이즈 비교
      if (meta.page_size !== this.pageSizeClass) return false;

      return true;
    },
    // 실제 적용될 수동 모드 (prop, item.manual_split_mode, 또는 breakpoints_meta)
    effectiveManualMode() {
      // 1. prop으로 전달된 값이 있으면 우선 사용
      if (this.manualMode === true) return true;
      // 2. item에 저장된 값 사용
      if (this.item?.manual_split_mode === true || this.item?.manual_split_mode === 1) return true;
      // 3. breakpoints_meta에 저장된 값 확인 (백엔드에서 manual_split_mode가 반환되지 않는 경우)
      const meta = this.savedBreakpointsMeta;
      if (meta?.manual_split_mode === true) return true;
      return false;
    },
  },
  methods: {
    sanitizeHtml,
    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },
    // 마크다운 변환 (볼드, 이탤릭)
    convertMarkdown(text) {
      if (!text) return '';
      let html = text;
      // ***볼드+이탤릭*** (순서 중요: 먼저 처리)
      html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
      // **볼드** 또는 __볼드__
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
      // *이탤릭* 또는 _이탤릭_
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
      html = html.replace(/_(.+?)_/g, '<em>$1</em>');
      return html;
    },
    // 간단한 해시 함수 (djb2)
    simpleHash(str) {
      let hash = 5381;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString(16);
    },
    // HTML을 블록 단위로 파싱
    parseContentToBlocks(htmlString) {
      if (!htmlString) return [];
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      return Array.from(tempDiv.children).map(el => el.outerHTML);
    },
    // 저장된 breakpoints로 콘텐츠 분할
    splitContentByBreakpoints(htmlString, breakpoints) {
      const blocks = this.parseContentToBlocks(htmlString);
      if (!blocks.length || !breakpoints?.length) return [htmlString];

      const pages = [];
      let currentPageBlocks = [];

      blocks.forEach((block, index) => {
        currentPageBlocks.push(block);

        // 현재 인덱스가 breakpoint면 페이지 나눔
        if (breakpoints.includes(index)) {
          pages.push(currentPageBlocks.join(''));
          currentPageBlocks = [];
        }
      });

      // 마지막 페이지 추가
      if (currentPageBlocks.length > 0) {
        pages.push(currentPageBlocks.join(''));
      }

      return pages;
    },
    // 현재 splitContent에서 breakpoints 추출
    extractBreakpoints() {
      // splitContent의 각 페이지에서 블록 수를 세어 breakpoint 위치 계산
      const breakpoints = [];
      let blockIndex = -1;

      this.splitContent.forEach((pageHtml, pageIndex) => {
        const blocks = this.parseContentToBlocks(pageHtml);
        blockIndex += blocks.length;

        // 마지막 페이지가 아니면 breakpoint 추가
        if (pageIndex < this.splitContent.length - 1) {
          breakpoints.push(blockIndex);
        }
      });

      return breakpoints;
    },
    // breakpoints와 메타데이터 emit (저장용)
    emitBreakpointsForSave() {
      const breakpoints = this.extractBreakpoints();
      const meta = {
        content_hash: this.contentHash,
        page_size: this.pageSizeClass,
        calculated_at: new Date().toISOString(),
        page_count: this.splitContent.length,
        manual_split_mode: this.effectiveManualMode, // 수동 분할 모드 저장
      };

      this.$emit('breakpoints-calculated', {
        page_breakpoints: breakpoints,
        breakpoints_meta: meta,
      });
    },
    // 상대 경로 이미지를 전체 URL로 변환
    normalizeContentToHtml(rawContent) {
      if (!rawContent) return "";
      if (typeof rawContent !== "string") return rawContent;
      const trimmed = rawContent.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
            return renderAstToHtml(parsed);
          }
        } catch (e) {
          // fall through
        }
      }
      return rawContent;
    },
    processImageUrls(htmlString) {
      if (!htmlString) return htmlString;
      // iskal/로 시작하는 상대 경로를 서버 URL로 변환 (/storage/ 경로 포함)
      return htmlString.replace(
        /src="(iskal\/[^"]+)"/g,
        `src="${SERVER_URL}/storage/$1"`
      );
    },
    // 수동 페이지 나누기 마커가 있는지 확인
    hasManualPageBreaks(htmlString) {
      if (!htmlString) return false;
      // page-break 클래스를 가진 div가 있는지 확인
      return /<div[^>]*class="[^"]*page-break[^"]*"[^>]*>/.test(htmlString);
    },
    // 수동 모드: --- 마커 위치에서 페이지 분할 + 참고문헌은 자동으로 새 페이지
    splitContentByManualBreaksOnly(htmlString) {
      if (!htmlString) return [''];

      // 1. 먼저 참고문헌 위치를 찾아서 분할 마커 삽입
      // references-title 클래스 또는 "참고문헌" 텍스트로 시작하는 요소 앞에서 분할
      let processedHtml = htmlString;

      // 참고문헌 제목 요소 앞에 분할 마커 삽입 (클래스 기반)
      processedHtml = processedHtml.replace(
        /(<div[^>]*class="[^"]*references-title[^"]*"[^>]*>)/gi,
        '<div class="page-break"></div>$1'
      );

      // 참고문헌 제목 요소 앞에 분할 마커 삽입 (텍스트 기반 - h2, h3, h4, p, div 등)
      // "참고문헌" 또는 "참고 문헌" 텍스트만 포함된 요소
      processedHtml = processedHtml.replace(
        /(<(?:h[2-6]|p|div)[^>]*>)\s*(참고\s*문헌|References)\s*(<\/(?:h[2-6]|p|div)>)/gi,
        '<div class="page-break"></div>$1$2$3'
      );

      // 2. page-break div를 기준으로 분할
      const parts = processedHtml.split(/<div[^>]*class="[^"]*page-break[^"]*"[^>]*><\/div>|<div[^>]*class="[^"]*page-break[^"]*"[^>]*\/>/gi);

      // 빈 페이지 제거하지 않고, 앞뒤 공백만 정리
      const pages = parts.map(part => part.trim()).filter((part, index) => {
        // 첫 페이지가 비어있으면 제거
        if (index === 0 && !part) return false;
        return true;
      });

      console.log('[Manual Mode] Split into', pages.length, 'pages (--- markers + 참고문헌 auto break)');
      return pages.length > 0 ? pages : [''];
    },
    // 하이브리드 분할: 수동 페이지 나누기 + 자동 높이 분할
    // 수동 나누기 위치에서 강제 분할 + 각 섹션 내에서 높이 초과 시 자동 분할
    splitContentHybrid(htmlString) {
      if (!htmlString) return [''];

      // 1. 수동 페이지 브레이크(---)로 먼저 분할 (page-break-only 제외)
      // page-break만 매칭하고 page-break-only는 제외
      const manualParts = htmlString.split(/<div[^>]*class="page-break"[^>]*><\/div>|<div[^>]*class="page-break"[^>]*\/>/gi);

      // 2. 각 수동 분할 부분에 대해 자동 높이 분할 적용
      const allPages = [];
      manualParts.forEach((part, index) => {
        const trimmedPart = part.trim();
        if (!trimmedPart) {
          // 첫 부분이 비어있으면 건너뜀
          if (index === 0) return;
          // 중간 빈 부분은 빈 페이지로 추가할 수도 있음 (선택)
          return;
        }

        // 이 섹션을 높이 기반으로 자동 분할
        const autoPages = this.splitContentByHeight(trimmedPart);
        allPages.push(...autoPages);
      });

      console.log('[Hybrid Split] Manual sections:', manualParts.length, '-> Total pages:', allPages.length);
      return allPages.length > 0 ? allPages : [''];
    },
    splitContentByHeight(htmlString) {
      const PAGE_HEIGHT = this.pageSizeConfig.height;
      const PAGE_WIDTH = this.pageSizeConfig.width;
      // A5: 75px, A4: 123px
      const HORIZONTAL_PADDING = this.pageSizeClass === 'a5' ? 75 : 123;
      // A5: 50px, A4: 75px
      const VERTICAL_PADDING = this.pageSizeClass === 'a5' ? 50 : 75;
      // 푸터+주석 영역 (A5: 주석 bottom:55px 기준, A4: 75px)
      // heightCalculator에서 이 높이만큼 콘텐츠 영역에서 제외
      const FOOTER_HEIGHT = this.pageSizeClass === 'a5' ? 55 : 75;

      return getSplitContentByHeight({
        content: htmlString,
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
        horizontalPadding: HORIZONTAL_PADDING,
        verticalPadding: VERTICAL_PADDING,
        footerHeight: FOOTER_HEIGHT,
        pageSize: this.pageSizeClass,
      });
    },
    // 콘텐츠에서 주석 ID 추출
    getFootnoteIdsFromContent(htmlContent) {
      if (!htmlContent) return [];

      const ids = [];
      // data-footnote-id 속성을 가진 sup 태그에서 ID 추출
      const regex = /data-footnote-id="(\d+)"/g;
      let match;
      while ((match = regex.exec(htmlContent)) !== null) {
        ids.push(match[1]);
      }
      return ids;
    },

    // 주석에서 번호 추출 (예: "1)" → "1", "2)" → "2")
    getFootnoteNumberFromSuperscript(superscript) {
      if (!superscript) return null;
      const match = superscript.toString().match(/^(\d+)/);
      return match ? match[1] : null;
    },

    getCommentsForPage(pageNumber) {
      return this.item.comments.filter(
        (comment) => comment.page === pageNumber
      );
    },

    commentsByPages(pageComments) {
      // 각 페이지의 콘텐츠에서 주석 ID를 추출하여 자동 배치
      const pages = new Array(this.splitContent.length).fill().map(() => []);

      // 전체 콘텐츠에서 모든 footnote ID 추출 (자동 배치 가능한 주석 목록)
      const allContent = this.splitContent.join('');
      const allFootnoteIds = this.getFootnoteIdsFromContent(allContent);

      // 각 페이지 콘텐츠에서 footnote ID 추출
      this.splitContent.forEach((pageContent, pageIndex) => {
        const footnoteIds = this.getFootnoteIdsFromContent(pageContent);

        // 해당 페이지에 있는 주석 ID에 맞는 주석들 찾기
        footnoteIds.forEach((footnoteId) => {
          const comment = pageComments.find((c) => {
            const commentNumber = this.getFootnoteNumberFromSuperscript(c.superscript);
            return commentNumber === footnoteId;
          });

          if (comment && !pages[pageIndex].includes(comment)) {
            pages[pageIndex].push(comment);
          }
        });
      });

      // 자동 배치되지 않은 주석들 중에서 콘텐츠에 마커가 없는 것만 수동 배치
      pageComments.forEach((comment) => {
        // 이미 배치된 주석인지 확인
        const isAlreadyPlaced = pages.some((pageComments) =>
          pageComments.includes(comment)
        );

        // 콘텐츠에 이 주석의 마커가 있는지 확인
        const commentNumber = this.getFootnoteNumberFromSuperscript(comment.superscript);
        const hasMarkerInContent = commentNumber && allFootnoteIds.includes(commentNumber);

        // 자동 배치 안됐고, 콘텐츠에 마커도 없고, 수동 페이지 번호가 있는 경우만 수동 배치
        // 단, page=1은 기본값이므로 마커가 없으면 배치하지 않음 (사용자가 명시적으로 2페이지 이상 지정한 경우만 수동 배치)
        if (!isAlreadyPlaced && !hasMarkerInContent && comment.page && comment.page > 1) {
          const pageIndex = comment.page - 1;
          if (pageIndex >= 0 && pageIndex < pages.length) {
            pages[pageIndex].push(comment);
          }
        }
      });

      return pages;
    },
    getPageNumber(index) {
      // 본문 페이지 번호 = 시작 페이지 + 앞선 페이지들(커버+초록) + 본문 인덱스
      return this.startPageNumber + this.prefixPageCount + index;
    },
    getReferencesPageNumber(refIndex = 0) {
      // 참고문헌 페이지 번호 = 시작 페이지 + 앞선 페이지들 + 본문 페이지 수 + 참고문헌 인덱스
      return this.startPageNumber + this.prefixPageCount + this.splitContent.length + refIndex;
    },
    // 내부 페이지 인덱스 (논문 내에서의 고정 위치, 1부터 시작)
    // 커버=1, 초록=2, 본문첫페이지=prefixPageCount+1, ...
    getInternalPageNumber(index) {
      return this.prefixPageCount + index + 1;
    },
    // 내부 페이지가 홀수인지 (좌우 여백 결정용)
    isInternalPageOdd(index) {
      return this.getInternalPageNumber(index) % 2 !== 0;
    },
    // 내부 페이지 클래스 (left/right)
    getInternalPageClass(index) {
      return this.isInternalPageOdd(index) ? 'left' : 'right';
    },
    // 참고문헌 내부 페이지 번호 (refIndex: 참고문헌 페이지 중 몇 번째인지)
    getReferencesInternalPageNumber(refIndex = 0) {
      return this.prefixPageCount + this.splitContent.length + 1 + refIndex;
    },
    // 참고문헌 페이지가 홀수인지
    isReferencesPageOdd(refIndex = 0) {
      return this.getReferencesInternalPageNumber(refIndex) % 2 !== 0;
    },
    // 참고문헌 페이지 클래스
    getReferencesInternalPageClass(refIndex = 0) {
      return this.isReferencesPageOdd(refIndex) ? 'left' : 'right';
    },
    // 참고문헌 콘텐츠 높이 기반 분할
    splitReferencesByHeight(htmlString) {
      const PAGE_HEIGHT = this.pageSizeConfig.height;
      const PAGE_WIDTH = this.pageSizeConfig.width;
      const HORIZONTAL_PADDING = this.pageSizeClass === 'a5' ? 75 : 123;
      const VERTICAL_PADDING = this.pageSizeClass === 'a5' ? 50 : 75;
      // 첫 페이지는 제목 공간(40px) 추가
      const FOOTER_HEIGHT = this.pageSizeClass === 'a5' ? 95 : 115;

      return getSplitContentByHeight({
        content: htmlString,
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
        horizontalPadding: HORIZONTAL_PADDING,
        verticalPadding: VERTICAL_PADDING,
        footerHeight: FOOTER_HEIGHT,
        pageSize: this.pageSizeClass,
      });
    },
    // 초록 페이지 업데이트
    updateAbstractPages() {
      const formattedHtml = this.formattedAbstract;
      if (!formattedHtml) {
        this.splitAbstractContent = [];
        return;
      }
      this.splitAbstractContent = this.splitAbstractByHeight(formattedHtml);
      // 페이지 수 재계산
      this.$emit("page-count", this.totalPageCount);
    },
    // 초록 콘텐츠 높이 기반 분할
    splitAbstractByHeight(htmlString) {
      const PAGE_HEIGHT = this.pageSizeConfig.height;
      const PAGE_WIDTH = this.pageSizeConfig.width;
      const HORIZONTAL_PADDING = this.pageSizeClass === 'a5' ? 45 : 70;
      const VERTICAL_PADDING = this.pageSizeClass === 'a5' ? 50 : 75;
      // 첫 페이지: 제목(40px), 마지막 페이지: 키워드 영역(50px)
      // 여유있게 footer height를 크게 잡음
      const FOOTER_HEIGHT = this.pageSizeClass === 'a5' ? 100 : 130;

      return getSplitContentByHeight({
        content: htmlString,
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
        horizontalPadding: HORIZONTAL_PADDING,
        verticalPadding: VERTICAL_PADDING,
        footerHeight: FOOTER_HEIGHT,
        pageSize: this.pageSizeClass,
      });
    },
    // 폰트 로딩 완료 대기 (일관된 높이 계산을 위해)
    async waitForFonts() {
      // document.fonts API 지원 확인
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          // 폰트 로딩 실패해도 계속 진행
          console.warn('Font loading check failed:', e);
        }
      }
      // 추가 안전장치: 짧은 지연
      return new Promise(resolve => setTimeout(resolve, 50));
    },

    /**
     * 현재 모드(effectiveManualMode)에 따라 페이지 재분할
     */
    async repaginateByMode() {
      const processedContent = this.processImageUrls(this.normalizeContentToHtml(this.item?.content));
      if (!processedContent) return;

      await this.waitForFonts();

      if (this.effectiveManualMode) {
        // 수동 모드 ON
        console.log('[Pagination] repaginateByMode: MANUAL mode');
        this.splitContent = this.splitContentByManualBreaksOnly(processedContent);
        this.usingSavedBreakpoints = false;
      } else if (this.hasManualPageBreaks(processedContent)) {
        // 하이브리드 모드
        console.log('[Pagination] repaginateByMode: HYBRID mode');
        this.splitContent = this.splitContentHybrid(processedContent);
        this.usingSavedBreakpoints = false;
      } else {
        // 자동 모드
        console.log('[Pagination] repaginateByMode: AUTO mode');
        this.splitContent = this.splitContentByHeight(processedContent);
        this.usingSavedBreakpoints = false;
        if (this.isComponentMounted) {
          await this.repaginateUntilNoOverlap();
        }
      }

      this.splitComments = this.commentsByPages(this.item?.comments || []);
      this.$emit("page-count", this.totalPageCount);
      // breakpoints_meta에 현재 모드 저장
      this.emitBreakpointsForSave();
    },

    /**
     * 렌더 후 페이지별 콘텐츠-푸터/주석 영역 침범 감지
     * scrollHeight 기반으로 실제 콘텐츠 높이 측정
     * @returns {Array<number>} 침범이 발생한 페이지 인덱스 배열
     */
    detectPageOverlaps() {
      if (!this.$el) {
        console.warn('[detectPageOverlaps] $el not available');
        return [];
      }

      const overlappedPages = [];
      const pageElements = this.$el.querySelectorAll('.page');

      // CSS에서 정의된 값
      const pageHeight = this.pageSizeConfig.height; // A5: 794, A4: 1122
      const verticalPadding = this.pageSizeClass === 'a5' ? 50 : 75;
      // 푸터 bottom 위치 (A5: 30px, A4: 45px)
      const footerBottomPos = this.pageSizeClass === 'a5' ? 30 : 45;
      // 주석 bottom 위치 (A5/A4 둘 다 55px)
      const commentsBottomPos = 55;

      // 콘텐츠 사용 가능 최대 높이 (상단 패딩 ~ 푸터/주석 시작 전)
      // 기본: pageHeight - topPadding - footerArea
      const baseMaxContentHeight = pageHeight - verticalPadding - footerBottomPos - 30; // 푸터 높이 약 30px

      console.log(`[detectPageOverlaps] ${pageElements.length} pages, maxHeight: ${baseMaxContentHeight}`);

      pageElements.forEach((pageEl, index) => {
        const contentEl = pageEl.querySelector('.page-content');
        const commentsEl = pageEl.querySelector('.page-comments-bottom');

        if (!contentEl) {
          return;
        }

        // 콘텐츠 실제 높이 (scrollHeight로 정확히 측정)
        const contentHeight = contentEl.scrollHeight;

        // 주석이 있으면 주석 영역 높이만큼 추가로 공간 필요
        let maxAllowedHeight = baseMaxContentHeight;
        if (commentsEl && commentsEl.children.length > 0) {
          const commentsHeight = commentsEl.scrollHeight;
          // 주석이 있으면 사용 가능 높이 감소
          maxAllowedHeight = pageHeight - verticalPadding - commentsBottomPos - commentsHeight - 15;
        }

        const gap = maxAllowedHeight - contentHeight;

        console.log(`[Page ${index}] contentH: ${contentHeight}, maxH: ${maxAllowedHeight}, gap: ${gap}`);

        // 콘텐츠가 허용 높이를 초과하면 침범
        if (contentHeight > maxAllowedHeight) {
          overlappedPages.push(index);
        }
      });

      return overlappedPages;
    },

    /**
     * 침범 발생 시 마지막 블록을 다음 페이지로 이동
     * @param {number} pageIndex - 침범이 발생한 페이지 인덱스
     * @returns {boolean} 수정 여부
     */
    moveLastBlockToNextPage(pageIndex) {
      if (pageIndex < 0 || pageIndex >= this.splitContent.length) return false;

      const currentContent = this.splitContent[pageIndex];
      if (!currentContent) return false;

      // 임시 컨테이너로 HTML 파싱
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = currentContent;

      const children = Array.from(tempDiv.children);
      if (children.length <= 1) {
        // 하나 이하면 이동 불가 (무한루프 방지)
        return false;
      }

      // 마지막 블록 추출
      const lastBlock = children.pop();
      const lastBlockHtml = lastBlock.outerHTML;

      // 현재 페이지 콘텐츠 업데이트 (마지막 블록 제외)
      const newCurrentContent = children.map(c => c.outerHTML).join('');

      // 다음 페이지 콘텐츠 앞에 추가
      const nextPageIndex = pageIndex + 1;
      let newNextContent = lastBlockHtml;

      if (nextPageIndex < this.splitContent.length) {
        // 기존 다음 페이지가 있으면 앞에 추가
        newNextContent = lastBlockHtml + this.splitContent[nextPageIndex];
      }

      // Vue 반응성을 위해 새 배열 생성
      const newSplitContent = [...this.splitContent];
      newSplitContent[pageIndex] = newCurrentContent;

      if (nextPageIndex < newSplitContent.length) {
        newSplitContent[nextPageIndex] = newNextContent;
      } else {
        // 새 페이지 추가
        newSplitContent.push(newNextContent);
      }

      this.splitContent = newSplitContent;
      return true;
    },

    /**
     * 반복적 페이지네이션: 침범 감지 후 자동 수정 (최대 15회 반복)
     */
    async repaginateUntilNoOverlap() {
      // 마운트되지 않았으면 건너뜀 (mounted에서 다시 호출됨)
      if (!this.isComponentMounted || !this.$el) {
        return;
      }

      // 이미 재페이지네이션 중이면 건너뜀
      if (this.isRepaginating) {
        console.log('[Pagination] Already repaginating, skipping');
        return;
      }

      this.isRepaginating = true;
      const MAX_ITERATIONS = 15; // 충분한 반복 횟수
      let iteration = 0;

      try {
        while (iteration < MAX_ITERATIONS) {
          // 렌더 완료 대기
          await this.$nextTick();
          // 추가 안전 지연 (DOM 업데이트 완료 보장)
          await new Promise(resolve => setTimeout(resolve, 30));

          const overlappedPages = this.detectPageOverlaps();
          console.log(`[Pagination] Iteration ${iteration}, overlaps:`, overlappedPages);

          if (overlappedPages.length === 0) {
            // 침범 없음 - 완료
            console.log('[Pagination] No overlaps, done!');
            break;
          }

          // 침범 발생한 페이지들 처리 (뒤에서부터 처리해야 인덱스 안 꼬임)
          let anyMoved = false;
          for (let i = overlappedPages.length - 1; i >= 0; i--) {
            const pageIdx = overlappedPages[i];
            const moved = this.moveLastBlockToNextPage(pageIdx);
            if (moved) {
              anyMoved = true;
            }
          }

          if (!anyMoved) {
            // 더 이상 이동 불가 - 무한루프 방지
            console.log('[Pagination] No blocks moved, stopping');
            break;
          }

          // 주석도 재배치
          this.splitComments = this.commentsByPages(this.item?.comments || []);
          iteration++;
        }

        if (iteration >= MAX_ITERATIONS) {
          console.warn('[Pagination] Max iterations reached, some pages may still overlap');
        }
      } finally {
        this.isRepaginating = false;
        // 최종 페이지 수 emit
        this.$emit("page-count", this.totalPageCount);
      }
    },
  },
  watch: {
    "item.content": {
      immediate: true,
      async handler(newContent) {
        // 폰트 로딩 완료 대기 (일관된 높이 계산을 위해)
        await this.waitForFonts();

        const processedContent = this.processImageUrls(this.normalizeContentToHtml(newContent));

        // 1. 수동 모드 ON (prop 또는 item.manual_split_mode) - 오직 --- 마커 위치에서만 분할
        if (this.effectiveManualMode) {
          console.log('[Pagination] Using MANUAL mode (--- only, no auto split)');
          this.splitContent = this.splitContentByManualBreaksOnly(processedContent);
          this.usingSavedBreakpoints = false;
          this.splitComments = this.commentsByPages(this.item?.comments || []);
          this.$emit("page-count", this.totalPageCount);
          // 수동 모드 정보도 breakpoints_meta에 저장
          if (this.isComponentMounted) {
            this.emitBreakpointsForSave();
          }
          return;
        }

        // 2. 수동 모드 OFF + --- 마커 있음 - 하이브리드 (수동 분할 + 자동 높이 분할)
        if (this.hasManualPageBreaks(processedContent)) {
          console.log('[Pagination] Using HYBRID mode (--- + auto split)');
          this.splitContent = this.splitContentHybrid(processedContent);
          this.usingSavedBreakpoints = false;
          this.splitComments = this.commentsByPages(this.item?.comments || []);
          this.$emit("page-count", this.totalPageCount);
          // 하이브리드 모드 정보도 breakpoints_meta에 저장
          if (this.isComponentMounted) {
            this.emitBreakpointsForSave();
          }
          return;
        }

        // 3. 저장된 breakpoints가 유효하면 사용
        if (this.isSavedBreakpointsValid) {
          console.log('[Pagination] Using saved breakpoints');
          this.splitContent = this.splitContentByBreakpoints(
            processedContent,
            this.savedBreakpoints
          );
          this.usingSavedBreakpoints = true;
          this.splitComments = this.commentsByPages(this.item?.comments || []);
          this.$emit("page-count", this.totalPageCount);
          return;
        }

        // 4. 저장된 breakpoints 없음 - 자동 계산
        console.log('[Pagination] Calculating new breakpoints (automatic)');
        this.usingSavedBreakpoints = false;

        // 1차 페이지네이션 (수식 기반)
        this.splitContent = this.splitContentByHeight(processedContent);

        // content가 변경되면 comments도 다시 계산 (자동 배치)
        this.splitComments = this.commentsByPages(this.item?.comments || []);

        // 2차 페이지네이션: 마운트 후에만 실행
        if (this.isComponentMounted) {
          await this.repaginateUntilNoOverlap();
          // 계산 완료 후 breakpoints emit (저장용)
          this.emitBreakpointsForSave();
        }
      },
    },
    "item.comments": {
      immediate: true,
      async handler(newComments) {
        // 주석이 변경되면 다시 계산 (자동 배치)
        this.splitComments = this.commentsByPages(newComments || []);

        // 주석 변경 후에도 침범 감지 + 수정 (마운트 후에만)
        if (this.isComponentMounted) {
          await this.repaginateUntilNoOverlap();
        }
      },
    },
    "item.abstract": {
      immediate: true,
      handler() {
        this.updateAbstractPages();
      },
    },
    "item.abstract_eng": {
      immediate: true,
      handler() {
        this.updateAbstractPages();
      },
    },
    "item.references": {
      immediate: true,
      handler(newReferences) {
        // 참고문헌이 변경되면 페이지 분할 계산
        if (!newReferences) {
          this.splitReferencesContent = [];
          return;
        }
        // formattedReferences를 가져와서 페이지 분할
        const formattedHtml = this.formattedReferences;
        if (!formattedHtml) {
          this.splitReferencesContent = [];
          return;
        }
        this.splitReferencesContent = this.splitReferencesByHeight(formattedHtml);
        // 페이지 수 재계산
        this.$emit("page-count", this.totalPageCount);
      },
    },
    // 수동 모드 토글 시 페이지 재분할
    manualMode: {
      immediate: false,
      async handler() {
        console.log('[Pagination] Manual mode prop changed to:', this.manualMode);
        await this.repaginateByMode();
      },
    },
    // item.manual_split_mode 변경 감지 (저장된 데이터 로드 시)
    "item.manual_split_mode": {
      immediate: false,
      async handler(newVal) {
        console.log('[Pagination] item.manual_split_mode changed to:', newVal);
        await this.repaginateByMode();
      },
    },
  },
  async mounted() {
    this.isComponentMounted = true;
    // 마운트 후 침범 감지 실행 (immediate 워처가 마운트 전에 실행되었으므로)
    await this.$nextTick();

    // 저장된 breakpoints 사용 중이면 재계산 불필요
    if (!this.usingSavedBreakpoints) {
      await this.repaginateUntilNoOverlap();
      // 계산 완료 후 breakpoints emit (저장용)
      this.emitBreakpointsForSave();
    }
  },
};
</script>

<style lang="scss" scoped>
$primary-font-color: #000000;
$secondary-font-color: #8b8b8b;
$background-color: #cbcccb;

// 페이지 사이즈 정의
$a4-width: 794px;
$a4-height: 1122px;
$a5-width: 559px;
$a5-height: 794px;

// A4 패딩
$a4-vertical-padding: 75px;
$a4-horizontal-padding: 70px;

// A5 패딩 (템플릿 기준: 40px 35px 비율 적용)
$a5-vertical-padding: 50px;
$a5-horizontal-padding: 45px;

// A5 좌우 페이지별 패딩 (제본 여백 고려)
$a5-horizontal-wide-padding: 55px;   // 제본 쪽 (안쪽)
$a5-horizontal-narrow-padding: 35px; // 바깥 쪽

$dotum-font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
$batang-font-family: "KoPubWorldBatang", "Noto Serif KR", serif;

/** ****************************************************************************************
 * [ 커버 페이지 ]
 *******************************************************************************************/
.cover-page {
  background-color: $background-color;
  position: relative;
  box-sizing: border-box;
  page-break-before: always;
  page-break-after: always;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .content-title {
      font-size: 20px;
      line-height: 1.4;
    }

    .content-subtitle {
      font-size: 14px;
      line-height: 1.4;
    }

    .content-author-container {
      margin-top: 80px;
    }

    .author-container {
      margin-top: 60px;
      margin-bottom: 150px;
      gap: 16px;
    }

    .content-author {
      &.name {
        font-size: 11px;
        line-height: 1.6;
      }

      &.organization {
        font-size: 11px;
        line-height: 1.6;
      }
    }

    .page-common-footer {
      bottom: 45px;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    padding: $a5-vertical-padding $a5-horizontal-padding;

    .content-title {
      font-size: 16px;
      line-height: 1.4;
    }

    .content-subtitle {
      font-size: 12px;
      line-height: 1.4;
    }

    .content-author-container {
      margin-top: 40px;
    }

    .author-container {
      margin-top: 40px;
      margin-bottom: 80px;
      gap: 10px;
    }

    .content-author {
      &.name {
        font-size: 10px;
        line-height: 1.5;
      }

      &.organization {
        font-size: 10px;
        line-height: 1.5;
      }
    }

    .page-common-footer {
      bottom: 30px;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }
  }
}

/** ****************************************************************************************
 * [ 초록 페이지 ]
 * AbstractPaperPage.vue 스타일 참조
 *******************************************************************************************/
.abstract-page {
  border: solid 1px #ccc;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  page-break-before: always;
  page-break-after: always;
  overflow: visible;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .abstract-title {
      font-size: 13.33px; // 10pt
      line-height: 29.33px; // 22pt
    }

    .abstract-text-ko,
    .abstract-text-en {
      font-size: 14px; // 10.5pt
      line-height: 29.33px; // 22pt
    }

    .abstract-keyword-area {
      bottom: $a4-vertical-padding + 8mm;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }

    .page-common-footer {
      bottom: 45px;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    padding: $a5-vertical-padding $a5-horizontal-padding;

    .abstract-title {
      font-size: 10pt;
      line-height: 22pt;
    }

    .abstract-text-ko,
    .abstract-text-en {
      font-size: 9pt;
      line-height: 15pt;
    }

    .abstract-keyword-area {
      bottom: $a5-vertical-padding + 6mm;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }

    .page-common-footer {
      bottom: 30px;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }
  }

  .abstract-title-section {
    margin-top: 30px;
  }

  .abstract-title {
    font-family: $dotum-font-family;
    font-weight: 700;
    color: #000;
    margin: 0;
  }

  .abstract-body {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .abstract-text-ko,
  .abstract-text-en {
    font-family: $batang-font-family;
    text-align: justify;
    word-break: break-all;
    letter-spacing: -0.03em;
    font-weight: 300;
    margin: 10px 0;
  }

  .abstract-keyword-area {
    position: absolute;
    font-family: $dotum-font-family;
    font-size: 9pt;

    .keyword-label-box {
      color: white;
      background-color: #8b8b8b;
      padding: 0px 5px;
    }

    .keyword-value {
      color: #000;
    }
  }
}

/** ****************************************************************************************
 * [ 참고문헌 페이지 ]
 *******************************************************************************************/
.references-page {
  border: solid 1px #ccc;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  page-break-before: always;
  page-break-after: always;
  font-family: $batang-font-family;

  .references-title {
    font-family: $dotum-font-family;
    font-weight: 700;
    margin-bottom: 12px;
    padding-bottom: 4px;
    border-bottom: 1px solid #333;
  }

  .references-content {
    ::v-deep {
      .reference-item {
        text-indent: -1.5em;
        padding-left: 1.5em;
        margin: 4px 0;
      }

      em, i {
        font-style: italic;
      }

      strong, b {
        font-weight: 700;
      }

      u {
        text-decoration: underline;
      }
    }
  }

  .page-common-footer {
    position: absolute;
    color: $primary-font-color;
    font-family: $dotum-font-family;
  }

  &.a4 {
    width: $a4-width;
    height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .references-title {
      font-size: 14pt;
    }

    .references-content ::v-deep .reference-item {
      font-size: 10pt;
      line-height: 16pt;
    }

    .page-common-footer {
      bottom: 45px;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }
  }

  &.a5 {
    width: $a5-width;
    height: $a5-height;
    padding: $a5-vertical-padding $a5-horizontal-padding;

    &.left {
      padding-left: $a5-horizontal-wide-padding;
      padding-right: $a5-horizontal-narrow-padding;

      .page-common-footer {
        left: $a5-horizontal-wide-padding;
        right: $a5-horizontal-narrow-padding;
      }
    }

    &.right {
      padding-left: $a5-horizontal-narrow-padding;
      padding-right: $a5-horizontal-wide-padding;

      .page-common-footer {
        left: $a5-horizontal-narrow-padding;
        right: $a5-horizontal-wide-padding;
      }
    }

    .references-title {
      font-size: 11pt;
    }

    .references-content ::v-deep .reference-item {
      font-size: 8pt;
      line-height: 12pt;
    }

    .page-common-footer {
      bottom: 30px;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }
  }
}

/** ****************************************************************************************
 * [ 본문 페이지 ]
 * 미리보기 페이지에 임시로 보더 색 추가
 *******************************************************************************************/
.page {
  border: solid 1px #ccc;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  page-break-before: always;
  page-break-after: always;

  &.a4 {
    width: $a4-width;
    height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .page-common-footer {
      bottom: 45px;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }
  }

  &.a5 {
    width: $a5-width;
    height: $a5-height;
    padding: $a5-vertical-padding $a5-horizontal-padding;

    // 홀수 페이지 (책 오른쪽): 왼쪽에 제본 여백 (넓음)
    &.left {
      padding-left: $a5-horizontal-wide-padding;
      padding-right: $a5-horizontal-narrow-padding;

      .page-common-footer {
        left: $a5-horizontal-wide-padding;
        right: $a5-horizontal-narrow-padding;
      }
    }

    // 짝수 페이지 (책 왼쪽): 오른쪽에 제본 여백 (넓음)
    &.right {
      padding-left: $a5-horizontal-narrow-padding;
      padding-right: $a5-horizontal-wide-padding;

      .page-common-footer {
        left: $a5-horizontal-narrow-padding;
        right: $a5-horizontal-wide-padding;
      }
    }

    .page-common-footer {
      bottom: 30px;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }
  }
}

/** ****************************************************************************************
 * [ 페이지 콘텐츠 ]
 *******************************************************************************************/
.page-content {
  height: auto;
  font-family: $batang-font-family;

  // A4 스타일 (디자인 가이드 기준)
  // 본문: 12pt/행간 20pt, 제목1: 18pt/26pt Bold, 제목1.1: 15pt/26pt Bold Sans
  // 표 상단: 10pt/14pt 백색, 표 내용: 9pt/14pt
  .a4 & {
    ::v-deep {
      p {
        font-size: 12pt;
        line-height: 20pt;
        text-align: justify;
        text-indent: 1em;
        margin: 6px 0;
        word-break: break-all;
      }

      h2 {
        font-size: 18pt;
        line-height: 26pt;
        font-weight: 700;
        margin: 20px 0 10px;
        padding-bottom: 4px;
        border-bottom: 1.5px solid #333;
      }

      h3 {
        font-size: 15pt;
        line-height: 26pt;
        font-weight: 700;
        font-family: $dotum-font-family;
        margin: 16px 0 8px;
      }

      h4 {
        font-size: 13pt;
        line-height: 22pt;
        font-weight: 600;
        margin: 12px 0 6px;
      }

      h5, h6 {
        font-size: 12pt;
        line-height: 20pt;
        font-weight: 600;
        margin: 10px 0 4px;
      }

      ol, ul {
        margin: 8px 0 8px 20px;
        font-size: 12pt;

        li {
          margin: 4px 0;
          line-height: 20pt;
        }
      }

      // 인용문 스타일 (A4)
      blockquote, .blockquote, .doc-blockquote {
        margin: 16px 0;
        padding: 14px 20px;
        background: #f9fafb;
        border-left: 4px solid #9ca3af;
        font-size: 11pt;
        line-height: 18pt;
        color: #4b5563;
        text-indent: 0;
      }

      // 표 스타일 (디자인 가이드 기준 - A4)
      // 표제목: Sans 11pt/22pt, 표 상단: 10pt/14pt 백색, 표 내용: 9pt/14pt
      table {
        width: auto !important;
        max-width: 100%;
        table-layout: auto;
        border-collapse: collapse;
        border: 1px solid #333;
        font-family: $dotum-font-family;
        font-size: 9pt;
        line-height: 14pt;
        margin: 14px 0;

        caption {
          padding: 8px;
          caption-side: top;
          text-align: center;
          font-size: 11pt;
          line-height: 22pt;
          font-weight: 400;
        }

        th, td {
          padding: 4px 6px;
          border: 1px solid #333;
          text-align: left;
          word-break: break-word;
        }

        thead th {
          background-color: #333;
          color: #fff;
          font-size: 10pt;
          line-height: 14pt;
          font-weight: 400;
          padding: 4px 6px;
          text-align: center;
        }

        thead td,
        tr:first-child th,
        tr:first-child td {
          text-align: center;
        }
      }

      // 그림 스타일 (디자인 가이드 기준 - A4)
      // 그림제목: Sans 11pt/22pt
      .figure-container {
        margin: 24px 0;
        text-align: center;

        // 이미지 래퍼 (테두리 적용) - 새 구조
        .figure-image-wrapper {
          display: inline-block;
          padding: 16px;
          border: 2px dashed #d1d5db;
          border-radius: 8px;

          img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
        }

        // 직접 자식 이미지 스타일 (래퍼 없는 기존 데이터 호환)
        > img {
          display: inline-block;
          max-width: calc(100% - 36px);
          height: auto;
          padding: 16px;
          border: 2px dashed #d1d5db !important;
          border-radius: 8px !important;
          box-shadow: none !important;
        }

        .figure-placeholder {
          display: inline-block;
          width: 80%;
          max-width: 500px;
          height: 200px;
          background: #f3f4f6;
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 14px;
        }

        .figure-caption {
          margin-top: 8px;
          font-family: $dotum-font-family;
          font-size: 11pt;
          line-height: 22pt;
          font-weight: 400;
          text-align: center;
        }
      }

      // 페이지 나누기 스타일 (A4)
      .page-break {
        page-break-after: always;
        break-after: page;
        height: 1px;
        margin: 30px 0;
        border-bottom: 1px dashed #ccc;
      }

      // 참고문헌 스타일 (A4) - 래퍼 없이 개별 스타일
      .references-title {
        margin-top: 40px;
        font-size: 14pt;
        font-weight: 700;
        margin-bottom: 16px;
      }

      .reference-item {
        margin: 6px 0;
        padding-left: 2em;
        text-indent: -2em;
        font-size: 10pt;
        line-height: 16pt;
        page-break-inside: avoid;
        break-inside: avoid;
      }
    }
  }

  // A5 스타일 (디자인 가이드 기준)
  // 본문: 9pt/행간 15pt, 제목1: 14pt/20pt Bold, 제목1.1: 11.5pt/20pt Bold Sans
  // 표 상단: 8pt/10.5pt 백색, 표 내용: 7pt/10.5pt
  .a5 & {
    ::v-deep {
      p {
        font-size: 9pt;
        line-height: 15pt;
        text-align: justify;
        text-indent: 1em;
        margin: 6px 0;
        word-break: break-all;
      }

      h2 {
        font-size: 14pt;
        line-height: 20pt;
        font-weight: 700;
        margin: 20px 0 10px;
        padding-bottom: 4px;
        border-bottom: 1.5px solid #333;
      }

      h3 {
        font-size: 11.5pt;
        line-height: 20pt;
        font-weight: 700;
        font-family: $dotum-font-family;
        margin: 16px 0 8px;
      }

      h4 {
        font-size: 10pt;
        line-height: 18pt;
        font-weight: 600;
        margin: 12px 0 6px;
      }

      h5, h6 {
        font-size: 9pt;
        line-height: 15pt;
        font-weight: 600;
        margin: 10px 0 4px;
      }

      ol, ul {
        margin: 8px 0 8px 20px;
        font-size: 9pt;

        li {
          margin: 4px 0;
          line-height: 15pt;
        }
      }

      // 인용문 스타일 (A5)
      blockquote, .blockquote, .doc-blockquote {
        margin: 12px 0;
        padding: 10px 14px;
        background: #f9fafb;
        border-left: 3px solid #9ca3af;
        font-size: 8pt;
        line-height: 13pt;
        color: #4b5563;
        text-indent: 0;
      }

      // 표 스타일 (디자인 가이드 기준 - A5)
      // 표제목: Sans 8.5pt/16.7pt, 표 상단: 8pt/10.5pt 백색, 표 내용: 7pt/10.5pt
      table {
        width: auto !important;
        max-width: 100%;
        table-layout: auto;
        border-collapse: collapse;
        border: 1px solid #333;
        font-family: $dotum-font-family;
        font-size: 7pt;
        line-height: 10.5pt;
        margin: 14px 0;

        caption {
          padding: 8px;
          caption-side: top;
          text-align: center;
          font-size: 8.5pt;
          line-height: 16.7pt;
          font-weight: 400;
        }

        th, td {
          padding: 3px 5px;
          border: 1px solid #333;
          text-align: left;
          word-break: break-word;
        }

        thead th {
          background-color: #333;
          color: #fff;
          font-size: 8pt;
          line-height: 10.5pt;
          font-weight: 400;
          text-align: center;
        }

        thead td,
        tr:first-child th,
        tr:first-child td {
          text-align: center;
        }
      }

      // 그림 스타일 (디자인 가이드 기준 - A5)
      // 그림제목: Sans 8.5pt/16.7pt
      .figure-container {
        margin: 0px 0;
        text-align: center;

        // 이미지 래퍼 (테두리 적용) - 새 구조
        .figure-image-wrapper {
          display: inline-block;
          padding: 12px;
          border: 2px dashed #d1d5db;
          border-radius: 8px;

          img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
        }

        // 직접 자식 이미지 스타일 (래퍼 없는 기존 데이터 호환)
        > img {
          display: inline-block;
          max-width: calc(100% - 28px);
          height: auto;
          padding: 12px;
          border: 2px dashed #d1d5db !important;
          border-radius: 8px !important;
          box-shadow: none !important;
        }

        .figure-placeholder {
          display: inline-block;
          width: 80%;
          max-width: 350px;
          height: 150px;
          background: #f3f4f6;
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 12px;
        }

        .figure-caption {
          margin-top: 6px;
          font-family: $dotum-font-family;
          font-size: 8.5pt;
          line-height: 16.7pt;
          font-weight: 400;
          text-align: center;
        }
      }

      // 페이지 나누기 스타일 (A5)
      .page-break {
        page-break-after: always;
        break-after: page;
        height: 1px;
        margin: 20px 0;
        border-bottom: 1px dashed #ccc;
      }

      // 참고문헌 스타일 (A5) - 래퍼 없이 개별 스타일
      .references-title {
        margin-top: 30px;
        font-size: 11pt;
        font-weight: 700;
        margin-bottom: 12px;
      }

      .reference-item {
        margin: 4px 0;
        padding-left: 1.5em;
        text-indent: -1.5em;
        font-size: 8pt;
        line-height: 12pt;
        page-break-inside: avoid;
        break-inside: avoid;
      }
    }
  }

  ::v-deep {
    strong {
      font-weight: 700;
    }

    em {
      font-style: italic;
    }

    h1, h2, h3, h4, h5, h6 {
      color: $primary-font-color;
      border: none !important;
    }

    img {
      border: none !important;
      border-radius: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
    }
  }
}

/** ****************************************************************************************
 * [ 푸터 ]
 *******************************************************************************************/
.page-common-footer {
  position: absolute;
  color: $primary-font-color;
  font-family: $dotum-font-family;
}

.footer-text-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-line {
  border: none;
  border-top: 0.5px solid $primary-font-color;
}

.footer-text {
  font-size: 8px;
  font-weight: 400;

  .a5 & {
    font-size: 7px;
  }
}

.page-number {
  font-size: 10px;
  font-weight: 400;

  .a5 & {
    font-size: 9px;
  }
}

/** ****************************************************************************************
 * [ 커버 요소 ]
 *******************************************************************************************/
.content-title-container {
  display: flex;
  flex-direction: column;
}

.content-title {
  font-weight: 600;
  color: $primary-font-color;
  font-family: $batang-font-family;
}

.content-subtitle {
  font-weight: 500;
  color: $primary-font-color;
  font-family: $batang-font-family;
  margin-top: 8px;
}

.content-author-container {
  display: flex;
  flex-direction: column;
}

.content-author-divider {
  width: 100%;
  border: none;
  border-top: 0.5px solid $primary-font-color;
}

.author-container {
  display: flex;
  flex-direction: column;
}

.content-author {
  color: $primary-font-color;
  font-family: $batang-font-family;

  &.name {
    font-weight: 500;
  }

  &.organization {
    font-weight: 300;
  }
}

.title-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/** ****************************************************************************************
 * [ 주석 (페이지 하단) ]
 *******************************************************************************************/
.page-comments-bottom {
  position: absolute;
  left: 0;
  right: 0;

  .a4 & {
    bottom: 55px;
    padding: 0 $a4-horizontal-padding;
  }

  .a5 & {
    bottom: 55px;
    padding: 0 $a5-horizontal-padding;

    &.left {
      padding: 0 $a5-horizontal-narrow-padding 0 $a5-horizontal-wide-padding;
    }

    &.right {
      padding: 0 $a5-horizontal-wide-padding 0 $a5-horizontal-narrow-padding;
    }
  }
}

.comments-divider {
  height: 1px;
  background-color: $primary-font-color;
  margin-bottom: 8px;

  .a4 & {
    width: 80px;
  }

  .a5 & {
    width: 60px;
    margin-bottom: 6px;
  }
}

// 각주 스타일 (디자인 가이드 기준)
// A5: Sans Regular 7pt/10pt, A4: Sans Regular 9pt/13pt
.page-comment {
  font-family: $dotum-font-family;
  font-weight: 400;
  color: $primary-font-color;
  text-align: left;
  margin-bottom: 3px;

  .a4 & {
    font-size: 9pt;
    line-height: 13pt;
  }

  .a5 & {
    font-size: 7pt;
    line-height: 10pt;
  }

  sup {
    font-size: 0.8em;
    margin-right: 2px;
  }
}

// PDF 출력용 print 미디어 쿼리
@media print {
  .page-content {
    ::v-deep {
      table {
        width: auto !important;
        max-width: 100% !important;
        table-layout: auto !important;
        page-break-inside: avoid;
      }

      th, td {
        word-break: break-word !important;
        overflow-wrap: break-word !important;
      }
    }
  }
}
</style>

<style>
/* EditPaperPagesV2 기준 본문 스타일 */
.paper-content {
  font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
}

.paper-content p {
  font-size: 9pt;
  line-height: 15pt;
  text-align: justify;
  text-indent: 1em;
  margin: 6px 0;
  word-break: break-all;
}

.paper-content h2 {
  font-size: 14pt;
  line-height: 20pt;
  font-weight: 700;
  margin: 20px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1.5px solid #333;
}

.paper-content h3 {
  font-size: 11.5pt;
  line-height: 20pt;
  font-weight: 700;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  margin: 16px 0 8px;
}

.paper-content h4 {
  font-size: 10pt;
  line-height: 18pt;
  font-weight: 600;
  margin: 12px 0 6px;
}

.paper-content h5,
.paper-content h6 {
  font-size: 9pt;
  line-height: 15pt;
  font-weight: 600;
  margin: 10px 0 4px;
}

.paper-content strong {
  font-weight: 700;
}

.paper-content em {
  font-style: italic;
}

.paper-content sup {
  font-size: 0.8em;
  vertical-align: super;
}

.paper-content ul,
.paper-content ol {
  margin: 8px 0 8px 20px;
  font-size: 9pt;
}

.paper-content li {
  margin: 4px 0;
  line-height: 15pt;
}

.paper-content .blockquote,
.paper-content blockquote,
.paper-content .doc-blockquote {
  margin: 12px 0;
  padding: 10px 14px;
  background: #f9fafb;
  border-left: 3px solid #9ca3af;
  font-size: 8pt!important;
  line-height: 13pt;
  color: #4b5563;
  text-indent: 0;
}

.paper-content table {
  width: 100% !important;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 1px solid #333;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 7pt;
  line-height: 9.5pt;
  margin: 14px 0;
}

.paper-content table caption {
  padding: 8px;
  caption-side: top;
  text-align: center;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
}

.paper-content th,
.paper-content td {
  padding: 2px 4px;
  border: 1px solid #333;
  text-align: center;
  word-break: break-word;
  line-height: 9.5pt;
}

.paper-content thead th {
  background-color: #333;
  color: #fff;
  font-size: 8pt;
  line-height: 10.5pt;
  font-weight: 400;
  text-align: center;
}

.paper-content thead td,
.paper-content tr:first-child th,
.paper-content tr:first-child td {
  text-align: center;
}

.paper-content .page-break {
  display: none;
}

.paper-content .references-title {
  margin-top: 30px;
  font-size: 11pt;
  font-weight: 700;
  margin-bottom: 12px;
}

.paper-content .reference-item {
  margin: 4px 0;
  padding-left: 1.5em;
  text-indent: -1.5em;
  font-size: 8pt;
  line-height: 12pt;
  page-break-inside: avoid;
  break-inside: avoid;
}

.paper-content .figure-container {
  margin: 0px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.paper-content .figure-container .figure-image-wrapper {
  display: inline-block;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
}

.paper-content .figure-container .figure-image-wrapper img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.paper-content .figure-container > img {
  display: inline-block;
  max-width: calc(100% - 28px);
  height: auto;
  padding: 12px;
  border: 2px dashed #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.paper-content .figure-container .figure-placeholder {
  display: inline-block;
  width: 80%;
  max-width: 350px;
  height: 150px;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
}

.paper-content .figure-container .figure-caption {
  order: -1;
  margin-top: 0;
  margin-bottom: 6px;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
  text-align: center;
}

.paper-content .figure-container.figure-caption-bottom .figure-caption {
  order: 0;
  margin-top: 6px;
  margin-bottom: 0;
}

.paper-content .box-style1,
.paper-content .box-style2,
.paper-content .box-style3,
.paper-content .box-style4 {
  margin: 8px 0;
}

.paper-content .box-style1-title .box-style1-title-text {
  display: inline-flex;
  margin-left: 28px;
  border-radius: 14px 14px 0 0;
  background-color: #e2e3e5;
  padding: 4px 12px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style1-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1.5px solid #cfd3d7;
  border-radius: 14px;
  padding: 14px;
  width: 100%;
  background-color: #fafafa;
}

.paper-content .box-style1-list-items {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.paper-content .box-style1-list-item {
  border-bottom: 1px solid #e1e4e8;
  padding: 1px 0;
  margin: 0;
}

.paper-content .box-style1-list-item:last-child {
  border-bottom: none;
}

.paper-content .box-style1-list-item p,
.paper-content .box-style1-list-item div,
.paper-content .box-style1-list-item span {
  margin: 0 !important;
  text-indent: 0 !important;
  line-height: 12pt;
  font-size: 8pt;
}

.paper-content .box-style2-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1.5px dotted #9aa0a6;
  border-radius: 10px;
  background-color: #f8f9fb;
  padding: 10px;
  width: 100%;
}

.paper-content .box-style2-card-title {
  display: inline-flex;
  border: 1px solid #9aa0a6;
  border-radius: 10px;
  background-color: #f5f6f8;
  padding: 0 8px;
  width: fit-content;
  color: #4b4f55;
  font-size: 8.5pt;
}

.paper-content .box-style2-card-text {
  color: #2f2f2f;
  line-height: 1.6;
  text-indent: 0;
  font-size: 8.5pt;
}

.paper-content .box-style3 {
  border: 1px solid #d2d6db;
  border-left: 5px solid #8b9096;
  border-radius: 12px;
  background-color: #f6f7f9;
  padding: 10px 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.paper-content .box-style3-header {
  margin-bottom: 4px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style3-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.paper-content .box-style3-list p {
  margin: 0;
  font-size: 8.5pt;
}

.paper-content .box-style4 {
  border: 1px solid #d2d5da;
  border-radius: 14px;
  background: linear-gradient(180deg, #f6f7f9 0%, #ffffff 100%);
  padding: 10px 12px;
}

.paper-content .box-style4-head {
  font-size: 8.5pt;
  margin-bottom: 4px;
  color: #2f2f2f;
}

.paper-content .box-style4-body p {
  color: #2f2f2f;
  line-height: 1.6;
  font-size: 8.5pt;
}

.paper-content img {
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}
</style>
